import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ShieldCheck,
  Check,
  ArrowRight,
  ArrowLeft,
  AlertCircle,
  PenTool,
  Type,
  RotateCcw,
} from 'lucide-react'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { useAuthStore } from '@personic/shared'

// Framework-specific legal text and acknowledgments
const FRAMEWORK_DATA: Record<
  string,
  {
    name: string
    fullName: string
    legalIntro: string
    acknowledgments: Array<{
      id: string
      text: string
      required: boolean
    }>
  }
> = {
  hipaa: {
    name: 'HIPAA',
    fullName: 'Health Insurance Portability and Accountability Act',
    legalIntro: `By completing this attestation, you confirm that your organization will comply with HIPAA requirements when using Personic Clinical product to process protected health information (PHI).`,
    acknowledgments: [
      {
        id: 'covered_entity',
        text: 'I certify that my organization is a Covered Entity or Business Associate under HIPAA.',
        required: true,
      },
      {
        id: 'baa_commitment',
        text: 'I acknowledge that a Business Associate Agreement (BAA) with Personic AI is required before processing PHI.',
        required: true,
      },
      {
        id: 'compliance_program',
        text: 'I certify that my organization maintains a HIPAA compliance program including policies, training, and risk assessments.',
        required: true,
      },
      {
        id: 'minimum_necessary',
        text: 'I commit to applying the Minimum Necessary standard when using personality insights for patient care.',
        required: true,
      },
    ],
  },
  eeoc: {
    name: 'EEOC',
    fullName: 'Equal Employment Opportunity Commission',
    legalIntro: `By completing this attestation, you commit to fair employment practices when using Personic Talent product for hiring and workforce decisions.`,
    acknowledgments: [
      {
        id: 'fair_hiring',
        text: 'I commit to fair hiring practices and acknowledge that personality insights are one factor among many in employment decisions.',
        required: true,
      },
      {
        id: 'adverse_impact',
        text: 'I commit to monitoring for adverse impact and will discontinue use if discriminatory patterns emerge.',
        required: true,
      },
      {
        id: 'non_discrimination',
        text: 'I certify that my organization maintains a non-discrimination policy compliant with Title VII, ADA, and ADEA.',
        required: true,
      },
      {
        id: 'human_decision',
        text: 'I acknowledge that all employment decisions will include human judgment and will not be made solely by automated systems.',
        required: true,
      },
    ],
  },
  batson: {
    name: 'Batson',
    fullName: 'Batson v. Kentucky Compliance',
    legalIntro: `By completing this attestation, you commit to non-discriminatory jury selection practices when using Personic Legal product.`,
    acknowledgments: [
      {
        id: 'methodology_acknowledgment',
        text: 'I understand the methodology limitations of personality profiling and commit to using insights as supplementary information only.',
        required: true,
      },
      {
        id: 'human_in_loop',
        text: 'I commit to human-in-the-loop review for all juror-related decisions with documented race-neutral reasoning.',
        required: true,
      },
      {
        id: 'expert_review',
        text: 'I acknowledge that personality insights should be reviewed by qualified legal professionals before use in jury selection.',
        required: true,
      },
      {
        id: 'non_discriminatory',
        text: 'I certify that personality insights will not be used to discriminate based on race, ethnicity, gender, religion, or national origin.',
        required: true,
      },
    ],
  },
  ferpa: {
    name: 'FERPA',
    fullName: 'Family Educational Rights and Privacy Act',
    legalIntro: `By completing this attestation, you commit to protecting student privacy when using Personic Education product.`,
    acknowledgments: [
      {
        id: 'educational_institution',
        text: 'I certify that my organization is an educational institution or authorized service provider under FERPA.',
        required: true,
      },
      {
        id: 'student_data_protection',
        text: 'I commit to protecting student education records and limiting disclosure according to FERPA requirements.',
        required: true,
      },
      {
        id: 'parental_consent',
        text: 'I acknowledge that proper consent procedures are required before creating personality profiles for students under 18.',
        required: true,
      },
      {
        id: 'legitimate_educational',
        text: 'I certify that personality insights will only be used for legitimate educational purposes.',
        required: true,
      },
    ],
  },
  finra: {
    name: 'FINRA',
    fullName: 'Financial Industry Regulatory Authority',
    legalIntro: `By completing this attestation, you commit to compliance with financial industry regulations when using Personic Finance product.`,
    acknowledgments: [
      {
        id: 'registered_firm',
        text: 'I certify that my organization is a FINRA-registered firm or operates under appropriate financial regulations.',
        required: true,
      },
      {
        id: 'suitability',
        text: 'I commit to documenting suitability determinations and maintaining records as required by FINRA Rule 2111.',
        required: true,
      },
      {
        id: 'compliance_program',
        text: 'I certify that my organization maintains a compliance program with supervisory procedures for AI-assisted tools.',
        required: true,
      },
      {
        id: 'disclosure',
        text: 'I commit to appropriate disclosure when personality insights inform customer recommendations.',
        required: true,
      },
    ],
  },
}

export default function AttestationForm() {
  const navigate = useNavigate()
  const { framework } = useParams<{ framework: string }>()
  const { user } = useAuthStore()
  const { attestationData, setAttestationData, setStep } = useOnboardingStore()

  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({})
  const [signatureType, setSignatureType] = useState<'typed' | 'drawn'>('typed')
  const [typedSignature, setTypedSignature] = useState('')
  const [representativeName, setRepresentativeName] = useState(user?.name || '')
  const [representativeTitle, setRepresentativeTitle] = useState('')
  const [representativeEmail, setRepresentativeEmail] = useState(user?.email || '')
  const [errors, setErrors] = useState<string[]>([])

  // Canvas for drawn signature
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false)

  useEffect(() => {
    setStep(5)
  }, [setStep])

  const frameworkData = framework ? FRAMEWORK_DATA[framework] : null

  if (!frameworkData) {
    return (
      <div className="text-center py-12">
        <p className="text-warm-600">Framework not found</p>
        <button
          onClick={() => navigate('/onboarding/attestation/frameworks')}
          className="mt-4 btn btn-secondary"
        >
          Back to Framework Selection
        </button>
      </div>
    )
  }

  const allAcknowledgmentsChecked = frameworkData.acknowledgments
    .filter((a) => a.required)
    .every((a) => checkedItems[a.id])

  const hasValidSignature =
    (signatureType === 'typed' && typedSignature.trim().length > 0) ||
    (signatureType === 'drawn' && hasDrawnSignature)

  const isFormValid =
    allAcknowledgmentsChecked &&
    hasValidSignature &&
    representativeName.trim().length > 0 &&
    representativeTitle.trim().length > 0

  // Canvas drawing functions
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    setIsDrawing(true)
    const rect = canvas.getBoundingClientRect()
    ctx.beginPath()
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top)
  }

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const rect = canvas.getBoundingClientRect()
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top)
    ctx.strokeStyle = '#1e3a5f'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.stroke()
    setHasDrawnSignature(true)
  }

  const stopDrawing = () => {
    setIsDrawing(false)
  }

  const clearCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setHasDrawnSignature(false)
  }

  const getSignatureData = (): string => {
    if (signatureType === 'typed') {
      return typedSignature
    }
    if (canvasRef.current) {
      return canvasRef.current.toDataURL()
    }
    return ''
  }

  const handleSubmit = async () => {
    const newErrors: string[] = []

    if (!allAcknowledgmentsChecked) {
      newErrors.push('Please acknowledge all required items')
    }
    if (!hasValidSignature) {
      newErrors.push('Please provide your signature')
    }
    if (!representativeName.trim()) {
      newErrors.push('Please enter your name')
    }
    if (!representativeTitle.trim()) {
      newErrors.push('Please enter your title')
    }

    if (newErrors.length > 0) {
      setErrors(newErrors)
      return
    }

    // Save attestation data
    const existingAttestations = attestationData?.frameworkAttestations || {}
    const newFrameworkAttestations = {
      ...existingAttestations,
      [framework!]: {
        acknowledgments: checkedItems,
        signatureType,
        signatureData: getSignatureData(),
        representativeName,
        representativeTitle,
        representativeEmail,
        submittedAt: new Date().toISOString(),
      },
    }

    const selectedFrameworks = attestationData?.selectedFrameworks || []
    const currentIndex = selectedFrameworks.indexOf(framework!)
    const nextIndex = currentIndex + 1

    setAttestationData({
      selectedFrameworks,
      frameworkAttestations: newFrameworkAttestations,
      currentFrameworkIndex: nextIndex,
    })

    // Check if there are more frameworks to attest to
    if (nextIndex < selectedFrameworks.length) {
      navigate(`/onboarding/attestation/form/${selectedFrameworks[nextIndex]}`)
    } else {
      navigate('/onboarding/attestation/confirmation')
    }
  }

  const handleBack = () => {
    const selectedFrameworks = attestationData?.selectedFrameworks || []
    const currentIndex = selectedFrameworks.indexOf(framework!)

    if (currentIndex > 0) {
      navigate(`/onboarding/attestation/form/${selectedFrameworks[currentIndex - 1]}`)
    } else {
      navigate('/onboarding/attestation/frameworks')
    }
  }

  const selectedFrameworks = attestationData?.selectedFrameworks || []
  const currentIndex = selectedFrameworks.indexOf(framework!) + 1
  const totalFrameworks = selectedFrameworks.length

  return (
    <div>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-copper-100 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-copper-600" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold text-midnight-900">
              {frameworkData.name} Attestation
            </h1>
            <p className="text-sm text-warm-500">
              Framework {currentIndex} of {totalFrameworks}
            </p>
          </div>
        </div>
        <p className="text-warm-600 text-sm">{frameworkData.fullName}</p>
      </motion.div>

      {/* Legal Intro */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-6 p-4 bg-midnight-50 rounded-xl"
      >
        <p className="text-sm text-midnight-700">{frameworkData.legalIntro}</p>
      </motion.div>

      {/* Errors */}
      {errors.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl"
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <ul className="text-sm text-red-700 space-y-1">
              {errors.map((error, i) => (
                <li key={i}>{error}</li>
              ))}
            </ul>
          </div>
        </motion.div>
      )}

      {/* Acknowledgments */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-6"
      >
        <h2 className="text-lg font-heading font-semibold text-midnight-900 mb-4">
          Required Acknowledgments
        </h2>
        <div className="space-y-3">
          {frameworkData.acknowledgments.map((ack) => (
            <label
              key={ack.id}
              className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                checkedItems[ack.id]
                  ? 'bg-copper-50 border-copper-500'
                  : 'bg-white border-warm-200 hover:border-copper-300'
              }`}
            >
              <div
                className={`w-5 h-5 rounded flex items-center justify-center border-2 flex-shrink-0 mt-0.5 transition-all ${
                  checkedItems[ack.id]
                    ? 'bg-copper-500 border-copper-500'
                    : 'bg-white border-warm-300'
                }`}
              >
                {checkedItems[ack.id] && <Check className="w-3 h-3 text-white" />}
              </div>
              <input
                type="checkbox"
                checked={checkedItems[ack.id] || false}
                onChange={(e) =>
                  setCheckedItems((prev) => ({
                    ...prev,
                    [ack.id]: e.target.checked,
                  }))
                }
                className="sr-only"
              />
              <span className="text-sm text-midnight-700">{ack.text}</span>
            </label>
          ))}
        </div>
      </motion.div>

      {/* Authorized Representative */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mb-6"
      >
        <h2 className="text-lg font-heading font-semibold text-midnight-900 mb-4">
          Authorized Representative
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-midnight-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={representativeName}
              onChange={(e) => setRepresentativeName(e.target.value)}
              className="w-full px-4 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-copper-400 focus:border-transparent"
              placeholder="John Smith"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-midnight-700 mb-1">
              Title / Position <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={representativeTitle}
              onChange={(e) => setRepresentativeTitle(e.target.value)}
              className="w-full px-4 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-copper-400 focus:border-transparent"
              placeholder="Chief Compliance Officer"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-midnight-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={representativeEmail}
              onChange={(e) => setRepresentativeEmail(e.target.value)}
              className="w-full px-4 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-copper-400 focus:border-transparent"
              placeholder="john.smith@company.com"
            />
          </div>
        </div>
      </motion.div>

      {/* Signature */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mb-8"
      >
        <h2 className="text-lg font-heading font-semibold text-midnight-900 mb-4">
          Digital Signature
        </h2>

        {/* Signature Type Toggle */}
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => setSignatureType('typed')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg border-2 transition-all ${
              signatureType === 'typed'
                ? 'bg-copper-50 border-copper-500 text-copper-700'
                : 'bg-white border-warm-200 text-warm-600 hover:border-copper-300'
            }`}
          >
            <Type className="w-4 h-4" />
            Type Signature
          </button>
          <button
            type="button"
            onClick={() => setSignatureType('drawn')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg border-2 transition-all ${
              signatureType === 'drawn'
                ? 'bg-copper-50 border-copper-500 text-copper-700'
                : 'bg-white border-warm-200 text-warm-600 hover:border-copper-300'
            }`}
          >
            <PenTool className="w-4 h-4" />
            Draw Signature
          </button>
        </div>

        {/* Typed Signature */}
        {signatureType === 'typed' && (
          <div>
            <input
              type="text"
              value={typedSignature}
              onChange={(e) => setTypedSignature(e.target.value)}
              className="w-full px-4 py-3 border border-warm-200 rounded-lg focus:ring-2 focus:ring-copper-400 focus:border-transparent text-xl"
              placeholder="Type your full name"
              style={{ fontFamily: 'cursive' }}
            />
            <p className="mt-2 text-xs text-warm-500">
              By typing your name, you agree that this constitutes your electronic signature.
            </p>
          </div>
        )}

        {/* Drawn Signature */}
        {signatureType === 'drawn' && (
          <div>
            <div className="relative border-2 border-warm-200 rounded-lg overflow-hidden bg-white">
              <canvas
                ref={canvasRef}
                width={400}
                height={150}
                className="w-full cursor-crosshair"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
              />
              {!hasDrawnSignature && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <p className="text-warm-400 text-sm">Draw your signature here</p>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={clearCanvas}
              className="mt-2 text-sm text-warm-600 hover:text-copper-600 flex items-center gap-1"
            >
              <RotateCcw className="w-3 h-3" />
              Clear signature
            </button>
          </div>
        )}
      </motion.div>

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="flex flex-col sm:flex-row gap-3"
      >
        <button
          onClick={handleBack}
          className="btn btn-secondary flex items-center justify-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={!isFormValid}
          className="flex-1 btn btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {currentIndex < totalFrameworks ? 'Submit & Continue' : 'Submit Attestation'}
          <ArrowRight className="w-4 h-4" />
        </button>
      </motion.div>
    </div>
  )
}
